import React from "react";
import { api } from "@/lib/api";
import { PackageCheck, CheckCircle2, CircleDot, AlertTriangle } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";

const ICON = {
  picked: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
  partial: <CircleDot className="w-4 h-4 text-amber-500" />,
  pending: <CircleDot className="w-4 h-4 text-slate-300" />,
  short: <AlertTriangle className="w-4 h-4 text-rose-500" />,
};
const LIST_STATUS = { picking: "Picking", ready_for_loading: "Ready to load", loaded: "Loaded" };
const n = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export function useJobPicked(legacyId) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get(`/tasks/${legacyId}/picked-items`).then((r) => setData(r.data)).catch(() => setData({ items: [], conflicts: [] }));
  }, [legacyId]);
  return data;
}

function PickRow({ it }) {
  const key = `${it.pick_list_id}-${it.line_id}`;
  return (
    <li className="flex items-start justify-between gap-3 py-2.5" data-testid={`picked-item-${key}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900">{it.title || it.sku}</div>
        <div className="text-[11px] text-slate-500">
          {[it.vehicle, it.shift_date, LIST_STATUS[it.list_status] || it.list_status].filter(Boolean).join(" · ")}
        </div>
        {it.short_reason && <div className="mt-0.5 text-xs text-rose-600">Short: {it.short_reason}</div>}
        {it.reserve_conflict && <div className="mt-0.5 text-xs text-rose-600">Stock could not be reserved</div>}
        {it.picked_at && <div className="text-[11px] text-slate-400">Picked {formatDateTime(it.picked_at)}{it.picked_by_name ? ` by ${it.picked_by_name}` : ""}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-1.5 text-sm font-semibold tabular-nums text-slate-900" data-testid={`picked-item-qty-${key}`}>
        {n(it.qty_picked)}/{n(it.qty_required)} {ICON[it.line_status]}
      </div>
    </li>
  );
}

export function JobPickedItems({ data }) {
  if (!data) return null;
  const { items = [], conflicts = [] } = data;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="task-picked-items">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center"><PackageCheck className="w-4 h-4" /></span>
        <div>
          <h2 className="text-base font-bold text-slate-900">Products picked</h2>
          <p className="text-[11px] text-slate-500">Live from warehouse pick lists for this job</p>
        </div>
      </div>
      {conflicts.length > 0 && (
        <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="picked-conflicts-banner">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-rose-700">
            <AlertTriangle className="w-4 h-4" /> {conflicts.length} picking conflict{conflicts.length > 1 ? "s" : ""}
          </div>
          <ul className="mt-1 space-y-0.5 text-xs text-rose-700">
            {conflicts.map((c, i) => <li key={i} data-testid={`picked-conflict-${c.code}-${i}`}>{c.message}</li>)}
          </ul>
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="picked-items-empty">Nothing picked from the warehouse for this job yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">{items.map((it) => <PickRow key={`${it.pick_list_id}-${it.line_id}`} it={it} />)}</ul>
      )}
    </section>
  );
}
